import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { AppStore } from '@app/app.store';
import { EmptyTranslationValue, TranslationValue } from '@app/shared/models/translation/translation-value.interface.';
import { EmptyTranslation, Translation } from '@app/shared/models/translation/translation.interface';
import { TranslateService } from '@ngx-translate/core';

export type LanguagePlatform = 'BACKOFFICE' | 'REMOTE' | 'B2BREMOTE' | 'B2BBACKOFFICE';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  /**
   * The constructor
   *
   * @param http an HttpClient instance
   */
  constructor(
    private http: HttpClient,
    private appStore: AppStore,
    private appConfigService: AppConfigService,
    protected translateService: TranslateService
  ) {}

  /**
   * Get the available languages
   */
  getAvailableLanguages() {
    return this.http.get<string[]>(`${this.appConfigService.getApi()}/public/translation/languages/available`);
  }

  getAvailableLanguagesByPlatform(platform: LanguagePlatform) {
    return this.http.get<string[]>(`${this.appConfigService.getApi()}/public/lang/available/${platform}`);
  }

  getAvailableLanguagesB2BRemote() {
    return this.getAvailableLanguagesByPlatform('B2BREMOTE');
  }

  getCurrentLocale(): string {
    if (this.appStore && this.appStore.state && this.appStore.state.login) {
      return this.appStore.state.login.language;
    } else {
      return 'en';
    }
  }

  getCurrentLangTranslation(tx: Translation) {
    if (!tx || !tx.translationValues) {
      return null;
    }

    const translationValue: TranslationValue = tx.translationValues.find(
      (tv: TranslationValue) => tv.lang === this.getCurrentLocale()
    );

    return translationValue ? translationValue.value : null;
  }

  getCurrentLangTxOrNoTx(tx: Translation) {
    if (!tx || !tx.translationValues) {
      return this.getNoTx();
    }

    const translationValue: TranslationValue = tx.translationValues.find(
      (tv: TranslationValue) => tv.lang === this.getCurrentLocale()
    );

    return !translationValue || !translationValue.value ? this.getNoTx() : translationValue.value;
  }

  getValueOrNoTx(value: string): string {
    return value ? value : this.getNoTx();
  }

  getNoTx(): string {
    return this.translateService.instant('language.noTranslation');
  }

  updateCurrentTx(tx: Translation, value: string) {
    let txValue: TranslationValue = tx.translationValues.find(tv => tv.lang === this.getCurrentLocale());
    if (!txValue) {
      txValue = new EmptyTranslationValue();
      txValue.lang = this.getCurrentLocale();
      tx.translationValues.push(txValue);
    }

    txValue.value = value ? value.trim() : null;
  }

  createTranslationForCurrentLang(value: string): Translation {
    const tx: Translation = new EmptyTranslation();
    tx.translationValues.push(this.createTranslationValueForCurrentLang(value));

    return tx;
  }

  createTranslationValueForCurrentLang(value: string): TranslationValue {
    const newTranslationValue = new EmptyTranslationValue();

    newTranslationValue.lang = this.getCurrentLocale();
    newTranslationValue.value = value;

    return newTranslationValue;
  }
}
